import type * as React from "react";

import { cn } from "../../lib/utils";

export interface FileInputProps extends React.ComponentProps<"input"> {
  /**
   * Accepted file types (MIME types or file extensions)
   * @example "image/*"
   * @example "application/pdf,.doc,.docx"
   */
  accept?: string;

  /**
   * Allow multiple file selection
   */
  multiple?: boolean;
}

/**
 * Base file input component that extends the standard HTML input element.
 * Uses the same styling as the Input component with file-specific enhancements.
 */
function FileInput({ className, accept, multiple, ...props }: FileInputProps) {
  return (
    <input
      accept={accept}
      className={cn(
        "h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs outline-none transition-[color,box-shadow] selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:font-medium file:text-foreground file:text-sm placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30",
        "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
        "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
        className
      )}
      data-slot="file-input"
      multiple={multiple}
      type="file"
      {...props}
    />
  );
}

export { FileInput };
